import { useMemo } from "react";
import { useQuery } from "react-query";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { Paywall } from "@/react/types/Paywall";
import { usePunditUserContext } from "@circle-react/contexts";

interface FetchParams {
  [key: string]: unknown;
  type?: string[];
}

interface UsePaywallsReturn {
  activePaywalls: Paywall[];
  allPaywalls: Paywall[];
  hasActivePaywalls: boolean;
  hasPaywalls: boolean;
  isLoading: boolean;
  isSuccess: boolean;
}

const fetchPaywalls = (params: FetchParams = {}) =>
  reactQueryGet<Paywall[]>(internalApi.paywalls.index({ params }));

export const usePaywallsApi = (
  fetchParams: FetchParams = {},
): UsePaywallsReturn => {
  const { currentCommunityMember } = usePunditUserContext();

  const queryKey = "PAYWALLS" + JSON.stringify(fetchParams);
  const {
    data: paywalls = [],
    isSuccess,
    isLoading,
  } = useQuery<Paywall[]>(queryKey, () => fetchPaywalls(fetchParams), {
    enabled: !!isCommunityAdmin(currentCommunityMember),
  });

  const activePaywalls = useMemo(() => {
    if (!paywalls) return [];
    return paywalls.filter(paywall => paywall.status === "active");
  }, [paywalls]);

  const hasActivePaywalls = useMemo(
    () => (activePaywalls ? activePaywalls?.length > 0 : false),
    [activePaywalls],
  );

  const hasPaywalls = useMemo(
    () => (paywalls ? paywalls?.length > 0 : false),
    [paywalls],
  );

  return {
    activePaywalls,
    isSuccess,
    hasActivePaywalls,
    hasPaywalls,
    isLoading,
    allPaywalls: paywalls,
  };
};
